import React, { useState } from "react";
import { getInitials } from "../lib/utils";
import { User } from "../lib/userLib";
import { useNavigate } from "react-router-dom";
import { resendWelcomeEmail } from "../lib/utilitiesLib";
import JsonModal from "./JsonModal";
import {
  sendTokenExpiringEmail,
  sendStatsDigestEmail,
  Inbox,
} from "../lib/inboxLib";
import { useSnackbar } from "../contexts/SnackbarContext";
import * as deletionsLib from "../lib/deletionsLib";
import { useAuthContext } from "../lib/authLib";

interface UserCardProps {
  user: User;
  inUserCollectionView?: boolean;
  onUpdate?: (user: User) => void;
  openMenuId?: string | null;
  setOpenMenuId?: (id: string | null) => void;
  menuRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  onUserDeleted?: () => void;
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  inUserCollectionView = false,
  openMenuId,
  setOpenMenuId,
  menuRefs,
  onUserDeleted,
}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { user: authUser } = useAuthContext();
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    title: string;
    data: any;
  }>({
    isOpen: false,
    title: "",
    data: null,
  });
  const [isHovering, setIsHovering] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedInbox, setSelectedInbox] = useState<Inbox | null>(null);

  const resendWelcomeEmailHandler = async () => {
    try {
      const response = await resendWelcomeEmail(user.id);
      setModalData({
        isOpen: true,
        title: "Welcome Email Status",
        data: { message: response },
      });
    } catch (error) {
      console.error("Error resending welcome email:", error);
      setModalData({
        isOpen: true,
        title: "Error",
        data: { error: "Failed to resend welcome email" },
      });
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".interactive")) {
      return;
    }
    navigate(`/users/${user.id}`);
  };

  const getTokenExpiryMessage = (inbox: Inbox): string => {
    if (!inbox.tokenCreatedAt || inbox.tokenCreatedAt === null) {
      return "This inbox has no active token";
    }

    if (!inbox.tokenExpiresAt || inbox.tokenExpiresAt === null) {
      return "This inbox's token will not expire";
    }

    const expiresAt = new Date(inbox.tokenExpiresAt);
    const now = new Date();
    const hoursRemaining = Math.floor(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60)
    );

    if (hoursRemaining <= 0) {
      return "This inbox's token has expired";
    }

    return `This inbox's token expires in ${hoursRemaining} hours`;
  };

  const handleSendAuthReminder = (inbox: Inbox) => {
    setSelectedInbox(inbox);
    setConfirmModalOpen(true);
  };

  const confirmSendAuthReminder = async () => {
    if (!selectedInbox) return;

    try {
      await sendTokenExpiringEmail(selectedInbox.id);
      showSnackbar("Auth reminder email sent successfully", "success");
      setConfirmModalOpen(false);
      setSelectedInbox(null);
    } catch (error) {
      console.error("Error sending auth reminder:", error);
      showSnackbar("Failed to send auth reminder email", "error");
    }
  };

  const handleSendStatsDigest = async (inbox: Inbox) => {
    try {
      await sendStatsDigestEmail(inbox.id, true);
      showSnackbar("Stats summary email sent successfully", "success");
    } catch (error) {
      console.error("Error sending stats digest:", error);
      showSnackbar("Failed to send stats summary email", "error");
    }
  };

  const handleDeleteUser = async (immediate: boolean) => {
    const action = immediate ? "immediately delete" : "schedule for deletion";
    if (
      !window.confirm(
        `Are you sure you want to ${action} this user account? ${immediate ? "This action cannot be undone." : "This will schedule deletion in 90 days."}`
      )
    ) {
      return;
    }

    try {
      const result = await deletionsLib.deleteUser(user.id, immediate);
      showSnackbar(result.message, "success");
      setOpenMenuId?.(null);
      onUserDeleted?.();
    } catch (error) {
      console.error("Error deleting user:", error);
      showSnackbar("Failed to delete user", "error");
    }
  };

  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-200 cursor-pointer
                ${isHovering ? "shadow-lg ring-2 ring-blue-200 dark:ring-blue-800" : "shadow-md"}`}
    >
      {user.isSuperAdmin && (
        <div className="absolute -top-2 -left-2 w-16 h-16 overflow-hidden">
          <div className="absolute top-2 left-0 -translate-y -translate-x-5 -rotate-45 bg-red-500 text-white text-xs py-1 px-6 shadow-md">
            Admin
          </div>
        </div>
      )}

      {/* Menu Button (only for superadmins in collection view) */}
      {authUser?.isSuperAdmin && inUserCollectionView && setOpenMenuId && (
        <div className="absolute top-2 right-2">
          <button
            className="interactive text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 font-bold w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === user.id ? null : user.id);
            }}
          >
            ⋮
          </button>
          {openMenuId === user.id && menuRefs && (
            <div
              ref={(el) => {
                if (menuRefs.current) {
                  menuRefs.current[user.id] = el;
                }
              }}
              className="interactive absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="py-1">
                <button
                  onClick={() => handleDeleteUser(false)}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Schedule Deletion (90 days)
                </button>
                <button
                  onClick={() => handleDeleteUser(true)}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  Delete Immediately
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center mb-4">
        {user.profilePicture ? (
          <img
            src={user.profilePicture}
            alt={`${user.firstName} ${user.lastName}`}
            className="w-16 h-16 rounded-full mr-4 object-cover"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-500 dark:bg-gray-300 flex items-center justify-center text-white dark:text-gray-600 text-3xl font-semibold">
            {getInitials(user.firstName, user.lastName)}
          </div>
        )}
        <div>
          <h3 className="text-lg ml-4 font-semibold">
            {user.firstName} {user.lastName}
          </h3>
          <p className="ml-4 text-gray-600 dark:text-gray-400">{user.email}</p>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        <p>Created: {new Date(user.createdAt).toLocaleDateString()}</p>
        {user.minimumDonation && <p>Min Donation: ${user.minimumDonation}</p>}
        {user.defaultDonation && (
          <p>Default Donation: ${user.defaultDonation}</p>
        )}
      </div>

      {/* Inboxes Section */}
      {user.inboxes && user.inboxes.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Inboxes:
          </h4>
          {user.inboxes.map((inbox) => (
            <div
              key={inbox.id}
              className="interactive bg-gray-50 dark:bg-gray-700 p-3 rounded text-sm"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {inbox.email}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {inbox.tokenCreatedAt && inbox.tokenExpiresAt && (
                      <>
                        Token expires:{" "}
                        {new Date(inbox.tokenExpiresAt).toLocaleString()}
                      </>
                    )}
                    {inbox.tokenCreatedAt && !inbox.tokenExpiresAt && (
                      <>Token: Never expires</>
                    )}
                    {!inbox.tokenCreatedAt && <>No active token</>}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {inbox.tokenCreatedAt && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendAuthReminder(inbox);
                    }}
                    className="interactive bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-1 px-3 rounded text-xs"
                  >
                    Send Auth Reminder
                  </button>
                )}
                {authUser?.isSuperAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendStatsDigest(inbox);
                    }}
                    className="interactive bg-blue-500 hover:bg-blue-600 text-white font-medium py-1 px-3 rounded text-xs"
                  >
                    Send Stats Summary
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex gap-2">
        {!inUserCollectionView && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              resendWelcomeEmailHandler();
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Resend welcome email
          </button>
        )}

        <a
          href={`/home?userId=${user.id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm"
        >
          View Dashboard
        </a>

        <a
          href={`/rules?userId=${user.id}`}
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm"
        >
          View Rules
        </a>

        <JsonModal
          isOpen={modalData.isOpen}
          onClose={() => setModalData({ isOpen: false, title: "", data: null })}
          title={modalData.title}
          data={modalData.data}
        />
      </div>

      {/* Confirmation Modal */}
      {confirmModalOpen && selectedInbox && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setConfirmModalOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Send Auth Reminder
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setConfirmModalOpen(false)}
              >
                <span className="sr-only">Close</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                {getTokenExpiryMessage(selectedInbox)}, and the system
                automatically emails a reauth reminder email with 48 hours
                remaining. Are you sure you want to manually send a reminder?
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmModalOpen(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmSendAuthReminder}
                className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Send Reminder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCard;
